import React from "react";
import { Link } from "react-router-dom";
import {
  MessageCircle,
  Mail,
  Phone,
  MapPin,
  HelpCircle,
} from "lucide-react";
import { KD9LogoHorizontal } from "@/components/KD9Logo";
import SupportButton from "@/components/SupportButton";
import SocialLinks from "@/components/SocialLinks";
import { KD9_CONTACT } from "@/lib/contact";
import { useLanguage } from "@/context/LanguageContext";

const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer
      className="relative mt-24 border-t"
      style={{
        borderColor: "var(--kd9-border)",
        background: "var(--kd9-bg-2)",
      }}
      data-testid="public-footer"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <KD9LogoHorizontal size={42} />
          <p
            className="mt-6 font-cormorant italic text-[18px] leading-relaxed max-w-sm"
            style={{ color: "var(--kd9-text)" }}
          >
            &ldquo;{t("footer.lema")}&rdquo;
          </p>
          <p
            className="mt-3 font-mont text-[12px] leading-relaxed max-w-sm"
            style={{ color: "var(--kd9-text-soft)" }}
          >
            KHAOS DELTA 9 — La energía que despierta lo ancestral.
          </p>
        </div>

        <div>
          <h4
            className="font-cinzel kd9-gold-text text-sm tracking-[0.3em] mb-5"
            data-testid="footer-contact-title"
          >
            {t("footer.contact").toUpperCase()}
          </h4>
          <ul
            className="space-y-3 font-mont text-sm"
            style={{ color: "var(--kd9-text-soft)" }}
          >
            <li className="flex items-center gap-3">
              <Phone size={14} style={{ color: "var(--kd9-accent)" }} />
              <a href={`tel:${KD9_CONTACT.phoneE164}`} className="hover:text-[color:var(--kd9-accent)]">{KD9_CONTACT.phoneDisplay}</a>
            </li>
            <li className="flex items-center gap-3">
              <Mail size={14} style={{ color: "var(--kd9-accent)" }} />
              <a href={`mailto:${KD9_CONTACT.email}`} className="hover:text-[color:var(--kd9-accent)]">{KD9_CONTACT.email}</a>
            </li>
            <li className="flex items-center gap-3">
              <MapPin size={14} style={{ color: "var(--kd9-accent)" }} />
              <a href={KD9_CONTACT.mapUrl} target="_blank" rel="noopener noreferrer" className="hover:text-[color:var(--kd9-accent)]">{KD9_CONTACT.location}</a>
            </li>
            <li>
              <a
                href={KD9_CONTACT.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 mt-2"
                style={{ color: "var(--kd9-success)" }}
                data-testid="footer-whatsapp-link"
              >
                <MessageCircle size={14} />
                <span>{t("footer.whatsapp")}</span>
              </a>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="font-cinzel kd9-gold-text text-sm tracking-[0.3em] mb-5">
            {t("footer.follow").toUpperCase()}
          </h4>
          <SocialLinks testIdPrefix="footer-social" className="mb-5" />
          <p
            className="font-mont text-[11px] leading-relaxed"
            style={{ color: "var(--kd9-text-soft)" }}
          >
            @khaosdelta9
          </p>
        </div>

        <div>
          <h4 className="font-cinzel kd9-gold-text text-sm tracking-[0.3em] mb-5">
            {t("footer.links").toUpperCase()}
          </h4>
          <ul
            className="space-y-2 font-mont text-sm"
            style={{ color: "var(--kd9-text-soft)" }}
          >
            <li>
              <Link
                to="/sobre"
                className="hover:text-[color:var(--kd9-accent)]"
                data-testid="footer-link-sobre"
              >
                {t("nav.sobre")}
              </Link>
            </li>
            <li>
              <Link
                to="/diagnosticos"
                className="hover:text-[color:var(--kd9-accent)]"
                data-testid="footer-link-diag"
              >
                {t("nav.diagnosticos")}
              </Link>
            </li>
            <li>
              <Link
                to="/academia"
                className="hover:text-[color:var(--kd9-accent)]"
              >
                {t("nav.academia")}
              </Link>
            </li>
            <li>
              <Link
                to="/marketplace"
                className="hover:text-[color:var(--kd9-accent)]"
              >
                {t("nav.marketplace")}
              </Link>
            </li>
            <li>
              <Link
                to="/contacto"
                className="hover:text-[color:var(--kd9-accent)]"
              >
                {t("nav.contacto")}
              </Link>
            </li>
            <li className="pt-3 mt-3 border-t" style={{ borderColor: "var(--kd9-border)" }}>
              <span
                className="block text-[10px] tracking-[0.3em] uppercase mb-2"
                style={{ color: "var(--kd9-text-soft)", opacity: 0.7 }}
              >
                {t("footer.policies")}
              </span>
              <Link to="#" className="block hover:text-[color:var(--kd9-accent)]">
                {t("footer.terms")}
              </Link>
              <Link to="#" className="block hover:text-[color:var(--kd9-accent)]">
                {t("footer.privacy")}
              </Link>
              <Link to="#" className="block hover:text-[color:var(--kd9-accent)] flex items-center gap-1">
                <HelpCircle size={12} /> {t("footer.faq")}
              </Link>
            </li>
          </ul>
        </div>
      </div>

      <div className="kd9-divider" />

      <div
        className="max-w-7xl mx-auto px-6 lg:px-10 py-6 text-center text-xs tracking-[0.25em] uppercase font-mont"
        style={{ color: "var(--kd9-text-soft)" }}
      >
        © {new Date().getFullYear()} KHAOS DELTA 9 — Todos los derechos reservados
      </div>

      <SupportButton />
    </footer>
  );
};

export default Footer;
